/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { Helmet } from 'react-helmet';

import Imgix from 'js/components/Imgix';
import { FormattedHTMLMessage } from 'js/lib/coursera.react-intl';
import user from 'js/lib/user';

import { Provider as CdsProvider, Grid, Link, Typography, Typography2 } from '@coursera/cds-core';
import { ExternalLinkIcon } from '@coursera/cds-icons';

import { AUTHENTICATION_MODAL_MODES } from 'bundles/authentication-common/constants';
import ChatLoader from 'bundles/common/components/ChatLoader';
import SimplifiedHeader from 'bundles/composable-page-header-consumer/header-renderers/SimplifiedHeader';
import UniversalPageFeatures from 'bundles/composable-page-header/components/UniversalPageFeatures';
import PageFooter from 'bundles/page-footer/components/PageFooter';
import AuthenticationOption from 'bundles/user-account/components/AuthenticationOption';

import LearnerChattingImage from 'images/hc/learner-chatting.jpg';

import _t from 'i18n!nls/help-redirects';

import 'css!bundles/styleguide/index';

const styles = {
  mainContainer: css`
    padding: var(--cds-spacing-600) var(--cds-spacing-300);
  `,
  pageTitle: css`
    font-size: var(--cds-font-size-600);
    line-height: var(--cds-line-height-650);
    margin-bottom: 1rem;
  `,
  descriptionSection: css`
    margin-top: 2rem;
  `,
};

/**
 * This page enables a learner to chat with the Boost.ai chat bot and potentially get transferred to a live customer service agent.
 * Our Salesforce-based learner help center website links over to here.
 */
const ChatWithUsPage = () => {
  if (!user.isAuthenticatedUser()) {
    return (
      <AuthenticationOption
        defaultMode={AUTHENTICATION_MODAL_MODES.LOGIN}
        utilizeWebRedirectOnClose
        onCloseRedirectURL="/hc/lhc-chat"
      />
    );
  }

  return (
    <CdsProvider locale="en">
      <Helmet title={_t('Chat with us | Coursera')} />
      <SimplifiedHeader />
      <UniversalPageFeatures />
      <main css={styles.mainContainer}>
        <Grid container spacing={48} justifyContent="space-between">
          <Grid item xs={12} sm={6}>
            <Typography variant="d1semibold" css={styles.pageTitle}>
              {_t('Chat with us')}
            </Typography>
            <Typography2 component="p" variant="bodyPrimary">
              {_t(
                'Coursera Bot can answer questions about your account, payments, and assessments and connect you with additional support.'
              )}
            </Typography2>
            <div css={styles.descriptionSection}>
              <ChatLoader isInlineButton />
            </div>
            <div css={styles.descriptionSection}>
              <Typography2 component="p" variant="bodyPrimary">
                Back to{' '}
                <Link href="https://learner.coursera.help" target="_blank" icon={<ExternalLinkIcon />}>
                  {_t('Learner Help Center')}
                </Link>
              </Typography2>
              <Typography2 component="p" variant="bodyPrimary">
                <FormattedHTMLMessage
                  message={_t('Go to {homePageLink}')}
                  homePageLink={<Link href="/">coursera.org</Link>}
                />
              </Typography2>
            </div>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Imgix
              src={LearnerChattingImage}
              alt=""
              css={{ display: 'block', margin: 'auto', width: '100%', maxWidth: '450px' }}
            />
          </Grid>
        </Grid>
      </main>
      <PageFooter />
    </CdsProvider>
  );
};

export default ChatWithUsPage;
